import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/api/client';
import { AlertTriangle, Download, Filter, RefreshCw } from 'lucide-react';
import { exportToCSV } from '@/utils/exportUtils';

const OUTCOMES = [
  'Replacement',
  'Upgraded Replacement',
  'Repaired',
  'Credit',
  'Warranty Rejected',
];

const outcomeBadge = (o) => {
  switch (o) {
    case 'Replacement': return 'success';
    case 'Upgraded Replacement': return 'info';
    case 'Repaired': return 'warning';
    case 'Credit': return 'info';
    case 'Warranty Rejected': return 'danger';
    default: return 'default';
  }
};

export const RMAReplacementLog = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({ supplier_id: '', outcome: '', start_date: '', end_date: '' });

  useEffect(() => {
    api.getSuppliers().then(r => setSuppliers(r.data || []));
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await api.getRMAReplacementLog(params);
      setRows(res.data.rows || []);
    } catch (e) {
      alert('Failed to load report');
    } finally { setLoading(false); }
  };

  const stats = useMemo(() => {
    const s = { total: rows.length };
    OUTCOMES.forEach(o => { s[o] = rows.filter(r => r.outcome === o).length; });
    return s;
  }, [rows]);

  const exportCSV = () => {
    const columns = [
      { key: 'job_id', label: 'RMA #' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'supplier_rma_no', label: 'Supplier RMA No.' },
      { key: 'replacement_doc_no', label: 'Doc No.' },
      { key: 'replacement_doc_date', label: 'Doc Date' },
      { key: 'replacement_date', label: 'Received Date' },
      { key: 'product_name', label: 'Original Product' },
      { key: 'original_serial', label: 'Original Serial' },
      { key: 'replacement_serial', label: 'Replacement Serial' },
      { key: 'replacement_product_name', label: 'Replacement Product' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'original_invoice_number', label: 'Original Invoice' },
      { key: 'outcome', label: 'Outcome' },
      { key: 'received_by', label: 'Received By' },
      { key: 'current_status', label: 'Status' },
      { key: 'remarks', label: 'Remarks' },
    ];
    exportToCSV(rows, `rma-replacement-log-${new Date().toISOString().slice(0, 10)}.csv`, columns);
  };

  return (
    <div className="space-y-6" data-testid="rma-replacement-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <AlertTriangle size={26} className="text-amber-500" /> RMA Replacement Log
          </h1>
          <p className="text-sm text-[var(--text-muted)]">
            Complete audit trail of supplier warranty outcomes — replacements, repairs, credits, and rejections.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load} disabled={loading} data-testid="refresh-report">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button variant="primary" onClick={exportCSV} disabled={rows.length === 0} data-testid="export-csv">
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="rma-log-stats">
        <StatCard label="Total" value={stats.total} />
        {OUTCOMES.map(o => (
          <StatCard key={o} label={o} value={stats[o]} tone={outcomeBadge(o)} />
        ))}
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <label className="text-xs font-medium mb-1 block flex items-center gap-1"><Filter size={12} /> Supplier</label>
            <Select value={filters.supplier_id}
              onChange={(e) => setFilters({ ...filters, supplier_id: e.target.value })}
              data-testid="filter-supplier">
              <option value="">All suppliers</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </div>
          <div className="w-52">
            <label className="text-xs font-medium mb-1 block">Outcome</label>
            <Select value={filters.outcome}
              onChange={(e) => setFilters({ ...filters, outcome: e.target.value })}
              data-testid="filter-outcome">
              <option value="">All outcomes</option>
              {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
            </Select>
          </div>
          <div>
            <Input label="From" type="date" value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              data-testid="filter-start-date" />
          </div>
          <div>
            <Input label="To" type="date" value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              data-testid="filter-end-date" />
          </div>
          <Button variant="secondary" onClick={load} data-testid="apply-filters">Apply</Button>
          <Button variant="ghost" onClick={() => { setFilters({ supplier_id: '', outcome: '', start_date: '', end_date: '' }); setTimeout(load, 0); }}>
            Reset
          </Button>
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Received</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Doc No.</TableHead>
              <TableHead>Doc Date</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Original Serial</TableHead>
              <TableHead>Replacement Serial</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Received By</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} className="text-center py-8 text-gray-500">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={11} className="text-center py-8 text-gray-500">
                No supplier RMA outcomes match your filters yet.
              </TableCell></TableRow>
            ) : (
              rows.map(r => (
                <TableRow key={r.rma_id} data-testid={`rma-log-row-${r.rma_id}`}>
                  <TableCell className="text-xs">{r.replacement_date || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="font-medium">{r.supplier || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="font-mono text-xs">{r.replacement_doc_no || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="text-xs">{r.replacement_doc_date || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="text-xs">
                    {r.product_name}
                    {r.replacement_product_name && r.replacement_product_name !== r.product_name && (
                      <div className="text-[10px] text-indigo-600 dark:text-indigo-300">→ {r.replacement_product_name}</div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.original_serial}</TableCell>
                  <TableCell className="font-mono text-xs">{r.replacement_serial || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="text-xs">{r.customer_name || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell><Badge variant={outcomeBadge(r.outcome)}>{r.outcome || 'Pending'}</Badge></TableCell>
                  <TableCell className="text-xs">{r.received_by || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="text-xs">{r.current_status}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

const StatCard = ({ label, value, tone }) => (
  <div className={`rounded-lg border p-3 border-[var(--border)] bg-[var(--surface)]`}>
    <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
    <div className={`text-xl font-bold ${tone === 'danger' ? 'text-red-600 dark:text-red-400' : tone === 'success' ? 'text-emerald-600 dark:text-emerald-400' : tone === 'warning' ? 'text-amber-600 dark:text-amber-400' : ''}`}>{value ?? 0}</div>
  </div>
);

export default RMAReplacementLog;
